import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronRight } from "lucide-react";
import { LOGOS } from "@/data/site";

const LINKS = [
  { to: "/", label: "Home", id: "home" },
  { to: "/experience", label: "Experience", id: "experience" },
  { to: "/pricing", label: "Pricing", id: "pricing" },
  { to: "/group-events", label: "Group Events", id: "group-events" },
  { to: "/edukarting", label: "EduKarting", id: "edukarting" },
  { to: "/gallery", label: "Gallery", id: "gallery" },
  { to: "/about", label: "About", id: "about" },
  { to: "/contact", label: "Contact", id: "contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-3 md:px-10 md:py-4">
          <Link to="/" data-testid="nav-logo-link" className="flex items-center py-1">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white p-1 md:h-14 md:w-14">
              <img src={LOGOS.icon} alt="Karting Bahamas" className="h-full w-full object-contain" />
            </div>
          </Link>

          <nav className="hidden items-center gap-7 xl:flex">
            {LINKS.map((l) => {
              const active = location.pathname === l.to;
              return (
                <Link
                  key={l.id}
                  to={l.to}
                  data-testid={`nav-${l.id}-link`}
                  className={`group relative font-display text-lg tracking-wide transition-colors ${
                    active ? "text-kbcyan" : "text-white/80 hover:text-white"
                  }`}
                >
                  {l.label}
                  <span
                    className={`absolute -bottom-1 left-0 h-[2px] bg-kbyellow transition-all duration-300 ${
                      active ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                  />
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/group-events")}
              data-testid="nav-book-event-btn"
              className="group hidden items-center gap-2 overflow-hidden rounded-xl bg-kbyellow px-5 py-2.5 font-display text-lg tracking-wide text-black transition-transform hover:scale-[1.03] sm:flex"
            >
              Book Event
              <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/15 bg-white/5 text-white xl:hidden"
              aria-label="Toggle menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 flex flex-col bg-[#050505] px-6 pt-28 xl:hidden"
          >
            <div className="grain-overlay" />
            <nav className="flex flex-col gap-1">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-${l.id}-link`}
                    className="flex items-center justify-between border-b border-white/10 py-4 font-display text-4xl tracking-wide text-white"
                  >
                    {l.label}
                    <ChevronRight className="h-6 w-6 text-kbcyan" />
                  </Link>
                </motion.div>
              ))}
            </nav>
            <button
              onClick={() => navigate("/group-events")}
              data-testid="mobile-book-event-btn"
              className="mt-8 rounded-xl bg-kbyellow py-4 text-center font-display text-2xl tracking-wide text-black"
            >
              Book An Event
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
